import React, { useEffect, useMemo, useState } from "react";

import { Link, useHistory, useLocation } from "react-router-dom";

import { useAuth } from "./../../hooks/auth";

import api from "./../../services/api";

import Header from "./../../components/Header/";
import Footer from "./../../components/Footer/";
import LoadingComponent from "./../../components/Loading/";
import VisitPropertyCard from "./../../components/VisitPropertyCard/";
import { hasOwnerScopedAccess } from "../../utils/canManageSubLogins";

import {
  Container,
  Content,
  EmptyVisitsContainer,
  VisitPropertyCardContainer,
  TitleAndImageContainer,
} from "./styles";

import scheduledVisitsImg from "./../../assets/pages/ScheduledVisits/scheduleImg.svg";
import { useRefresh } from "../../hooks/refresh";
import { getApiErrorMessage, getHttpStatus } from "../../utils/authRedirect";

interface IAddress {
  id: number;
  address: string;
  neighborhood: string;
  city: string;
  uf: string;
}

interface IPhotos {
  id: number;
  url_l: string;
  url_m: string;
  url_s: string;
}

interface IApiResult {
  id: number;
  schedule_id: number;
  consultant_id: number;
  first_name: string;
  title: string;
  date: string;
  address: IAddress;
  photos: IPhotos[];
}

interface IError {
  message: string;
  isForbidden?: boolean;
}

type VisitAction = "reschedule" | "cancel";

function parseVisitActionFromSearch(search: string): {
  action: VisitAction | null;
  scheduleId: number | null;
} {
  const params = new URLSearchParams(search);
  const action = params.get("action");
  const rawId = params.get("id") ?? params.get("schedule_id");

  const scheduleId = rawId ? Number(rawId) : null;
  const parsedScheduleId =
    scheduleId !== null && !Number.isNaN(scheduleId) ? scheduleId : null;

  if (action === "reschedule" || action === "cancel") {
    return { action, scheduleId: parsedScheduleId };
  }

  return { action: null, scheduleId: parsedScheduleId };
}

export default function ScheduledVisits() {
  const { user, access_token } = useAuth();
  const { refresh } = useRefresh();

  const history = useHistory();
  const location = useLocation();

  const [apiResult, setApiResult] = useState<IApiResult[]>([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<IError>();

  const { action: pendingAction, scheduleId: pendingScheduleId } = useMemo(
    () => parseVisitActionFromSearch(location.search),
    [location.search]
  );

  const clearActionQueryParams = () => {
    if (!location.search) {
      return;
    }

    history.replace(location.pathname);
  };

  useEffect(() => {
    if (!user || !access_token) {
      setLoading(false);
      return;
    }

    let isCancelled = false;

    async function loadApi() {
      setLoading(true);
      setError(undefined);

      api.defaults.headers.authorization = `Bearer ${access_token}`;

      try {
        const responseApiVisits = await api.get("/api/client/visits");

        if (!isCancelled) {
          setApiResult(responseApiVisits.data.data ?? []);
        }
      } catch (err) {
        if (isCancelled) {
          return;
        }

        const status = getHttpStatus(err);

        if (status === 401) {
          return;
        }

        if (status === 403) {
          setError({
            message: getApiErrorMessage(
              err,
              "Você não tem permissão para acessar esta página."
            ),
            isForbidden: true,
          });
          return;
        }

        setError({
          message: getApiErrorMessage(
            err,
            "Não foi possível carregar suas visitas agendadas. Tente novamente."
          ),
        });
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    }

    loadApi();

    return () => {
      isCancelled = true;
    };
  }, [access_token, user, refresh]);

  return (
    <Container>
      <Header hasBorder hasSearchBar />

      {!!loading && <LoadingComponent />}

      {!loading && (
        <Content>
          {!!user && (
            <h3>
              {hasOwnerScopedAccess(user) ? "PROPRIETÁRIO" : "INQUILINO"}
            </h3>
          )}
          <h1>Visitas Agendadas</h1>

          {apiResult.length >= 1 && !error && (
            <VisitPropertyCardContainer>
              {apiResult.map((result) => (
                <div key={result.schedule_id}>
                  <VisitPropertyCard
                    property_id={result.id}
                    first_name={result.first_name}
                    title={result.title}
                    date={result.date}
                    address={result.address}
                    photos={result.photos}
                    schedule_id={result.schedule_id}
                    consultant_id={result.consultant_id}
                    initialAction={
                      pendingScheduleId === result.schedule_id
                        ? pendingAction ?? undefined
                        : undefined
                    }
                    onInitialActionHandled={clearActionQueryParams}
                  />
                </div>
              ))}
            </VisitPropertyCardContainer>
          )}

          {apiResult.length === 0 && !error && (
            <EmptyVisitsContainer>
              <h1>Você ainda não tem nenhuma visita agendada.</h1>
            </EmptyVisitsContainer>
          )}
          {error && (
            <EmptyVisitsContainer>
              <h1>{error.message}</h1>
              {error.isForbidden && (
                <p>
                  <Link to="/">Voltar para a página inicial</Link>
                </p>
              )}
            </EmptyVisitsContainer>
          )}

          <TitleAndImageContainer>
            {!!user && (
              <h4>
                {hasOwnerScopedAccess(user) ? "PROPRIETÁRIO" : "INQUILINO"}
              </h4>
            )}

            <h1>Visitas Agendadas</h1>

            <img src={scheduledVisitsImg} alt="scheduledVisitsImg" />
          </TitleAndImageContainer>
        </Content>
      )}

      <Footer />
    </Container>
  );
}
